<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useCalculateOrderMoney, useFilters, useOrderPayments } from "@/composables";

import type { OrderExpense, OrderPayment } from "@/api/orders/dto/order.dto";
import type { TravelOrderDto } from "@/api/trips/dto/trip.dto";

const { t } = useI18n();
const { mdash } = useFilters();
const { formatOrderPaymentRate } = useOrderPayments();

const props = defineProps<{
  orderPayments: OrderPayment[];
  orderExpenses: OrderExpense[];
  travelOrders: TravelOrderDto[];
}>();

const {
  hasTravelOrders,
  totalInvoiceRate,
  totalExpenses,
  totalOrderRate,
  totalDriverRate,
  companyProfit,
  dispatcherProfit,
} = useCalculateOrderMoney(
  () => props.orderPayments,
  () => props.orderExpenses,
  () => props.travelOrders,
);

const totals = computed(() => [
  {
    key: "total-invoice-rate",
    label: t("payments.total-invoice-rate"),
    value: formatOrderPaymentRate(totalInvoiceRate.value),
  },
  { key: "total-expenses", label: t("payments.total-expenses"), value: formatOrderPaymentRate(totalExpenses.value) },
  {
    key: "total-order-rate",
    label: t("payments.total-order-rate"),
    value: formatOrderPaymentRate(totalOrderRate.value),
  },
  {
    key: "total-driver-rate",
    label: t("payments.total-driver-rate"),
    value: formatTripMoneyDependent(totalDriverRate.value),
  },
]);

const profits = computed(() => [
  { key: "company-profit", label: t("payments.company-profit"), value: formatTripMoneyDependent(companyProfit.value) },
  {
    key: "dispatcher-profit",
    label: t("payments.dispatcher-profit"),
    value: formatTripMoneyDependent(dispatcherProfit.value),
  },
]);

function formatTripMoneyDependent(value: number): string {
  return hasTravelOrders.value ? formatOrderPaymentRate(value) : mdash(null);
}
</script>

<template>
  <div class="d-flex justify-end order-money-result">
    <div class="d-flex flex-column order-money-result__figures">
      <div
        v-for="line in totals"
        :key="line.key"
        :data-qa="`order-money-result-${line.key}`"
        class="d-flex align-center justify-space-between col-gap-12"
      >
        <span class="text-grey-darken-1 font-weight-500">{{ line.label }}:</span>
        <span class="font-weight-600">{{ line.value }}</span>
      </div>

      <div class="order-money-result__divider" />

      <div
        v-for="line in profits"
        :key="line.key"
        :data-qa="`order-money-result-${line.key}`"
        class="d-flex align-center justify-space-between font-weight-600"
      >
        <span>{{ line.label }}:</span>
        <span>{{ line.value }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-money-result {
  padding-right: 16px;

  &__figures {
    gap: 8px;
  }

  &__line {
    line-height: 1.4;
    color: $grey-navy;
    white-space: nowrap;
  }

  &__value {
    text-align: right;
  }

  &__divider {
    height: 1px;
    background-color: $grey-lighter;
  }
}
</style>
